<script setup lang="ts">
import { ref, computed, toRaw, onMounted, onUnmounted, watch } from 'vue';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/store/auth.store';
import { useTheme } from '@/composables/useTheme';
import axiosInstance from '@/services/interceptor.services';
import { useGlobalSearch } from '@/composables/useGlobalSearch';

const emit = defineEmits<{ (e: 'menu-click'): void }>();

const authStore = useAuthStore();
const { isDark, toggle } = useTheme();
const router = useRouter();
const { locale, t } = useI18n({ useScope: 'global' });
const qc = useQueryClient();

// ── User ──────────────────────────────────────────────────────────────────────
const { data: me } = useQuery({
  queryKey: ['restaurant'],
  queryFn: () => authStore.getMeInfo(),
});

const user = () => toRaw(me.value);
const fullName = () => {
  const u = user()?.user;
  if (!u) return 'Admin';
  const first = u.firstname ?? '';
  const last = u.lastname ? u.lastname.charAt(0) + '.' : '';
  return `${first} ${last}`.trim();
};
const userRole = () => user()?.user?.roles?.[0]?.label ?? 'Super Admin';
const userPhoto = () => user()?.user?.photoUrl ?? null;

// ── Profile drawer ───────────────────────────────────────────────────────────
const profileOpen = ref(false);
const profileTab = ref<'info' | 'photo' | 'password'>('info');

const profileForm = ref({
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
});
const photoFile = ref<File | null>(null);
const photoPreview = ref<string | null>(null);
const profileSaving = ref(false);
const profileSaved = ref(false);
const profileError = ref<string | null>(null);
const photoSaving = ref(false);
const photoSaved = ref(false);
const photoError = ref<string | null>(null);
const passwordForm = ref({ currentSecret: '', secret: '', confirm: '' });
const passwordSaving = ref(false);
const passwordSaved = ref(false);
const passwordError = ref<string | null>(null);

function openProfile() {
  const u = user()?.user;
  const rawPhone = u?.phone;
  profileForm.value = {
    firstname: u?.firstname ?? '',
    lastname: u?.lastname ?? '',
    email: u?.email ?? '',
    phone: rawPhone && rawPhone !== 'null' ? rawPhone : '',
  };
  photoPreview.value = userPhoto();
  profileTab.value = 'info';
  profileSaved.value = false;
  profileError.value = null;
  photoSaved.value = false;
  photoError.value = null;
  passwordForm.value = { currentSecret: '', secret: '', confirm: '' };
  passwordSaved.value = false;
  passwordError.value = null;
  profileOpen.value = true;
  document.body.style.overflow = 'hidden';
}

function closeProfile() {
  profileOpen.value = false;
  photoFile.value = null;
  document.body.style.overflow = '';
}

async function saveProfileInfo() {
  profileSaving.value = true;
  profileError.value = null;
  try {
    const payload: Record<string, string> = {
      firstname: profileForm.value.firstname,
      lastname: profileForm.value.lastname,
      email: profileForm.value.email,
    };
    const phone = profileForm.value.phone.trim();
    if (phone && phone !== 'null') {
      payload.phone = phone;
    }
    await axiosInstance.put('auth/update.info', payload, {
      headers: { 'x-client-app': 'admin-back-office' },
    });
    qc.invalidateQueries({ queryKey: ['restaurant'] });
    profileSaved.value = true;
    setTimeout(() => {
      profileSaved.value = false;
    }, 2500);
  } catch (e: any) {
    profileError.value =
      e?.response?.data?.message ?? t('common.errorOccurred');
  } finally {
    profileSaving.value = false;
  }
}

async function savePassword() {
  passwordError.value = null;
  if (!passwordForm.value.currentSecret) {
    passwordError.value = t('profile.currentPasswordRequired');
    return;
  }
  if (!passwordForm.value.secret || passwordForm.value.secret.length < 8) {
    passwordError.value = t('profile.passwordTooShort');
    return;
  }
  if (passwordForm.value.secret !== passwordForm.value.confirm) {
    passwordError.value = t('profile.passwordMismatch');
    return;
  }
  passwordSaving.value = true;
  try {
    await axiosInstance.put(
      'auth/update.password',
      {
        currentSecret: passwordForm.value.currentSecret,
        secret: passwordForm.value.secret,
      },
      {
        headers: { 'x-client-app': 'admin-back-office' },
      },
    );
    passwordSaved.value = true;
    passwordForm.value = { currentSecret: '', secret: '', confirm: '' };
    setTimeout(() => {
      passwordSaved.value = false;
    }, 2500);
  } catch (e: any) {
    passwordError.value =
      e?.response?.data?.message ?? t('common.errorOccurred');
  } finally {
    passwordSaving.value = false;
  }
}

function onPhotoChange(e: Event) {
  const file = (e.target as HTMLInputElement).files?.[0];
  if (!file) return;
  photoFile.value = file;
  const reader = new FileReader();
  reader.onload = (ev) => {
    photoPreview.value = ev.target?.result as string;
  };
  reader.readAsDataURL(file);
}

async function savePhoto() {
  if (!photoFile.value) return;
  photoSaving.value = true;
  photoError.value = null;
  try {
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (ev) => resolve(ev.target?.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(photoFile.value!);
    });
    await axiosInstance.put(
      'auth/update.photo',
      { file: base64 },
      {
        headers: { 'x-client-app': 'admin-back-office' },
      },
    );
    qc.invalidateQueries({ queryKey: ['restaurant'] });
    photoSaved.value = true;
    setTimeout(() => {
      photoSaved.value = false;
    }, 2500);
  } catch (e: any) {
    photoError.value = e?.response?.data?.message ?? t('common.errorOccurred');
  } finally {
    photoSaving.value = false;
  }
}

// ── Recherche ─────────────────────────────────────────────────────────────────
const search = ref('');
const activeIdx = ref(0);
const { searchOpen, closeSearch: _closeSearch } = useGlobalSearch();
const searchInput = ref<HTMLInputElement | null>(null);

watch(searchOpen, (open) => {
  if (open) {
    search.value = '';
    activeIdx.value = 0;
    setTimeout(() => searchInput.value?.focus(), 10);
  }
});

// Toutes les pages du sidebar (même structure que NAV_DEF dans AppSidebar)
const ALL_NAV = computed(() => [
  // Pilotage
  {
    label: t('sidebar.items.dashboard'),
    icon: 'pi-th-large',
    path: '/',
    group: t('sidebar.groups.pilotage'),
  },
  {
    label: t('sidebar.items.analytics'),
    icon: 'pi-chart-line',
    path: '/analytics',
    group: t('sidebar.groups.pilotage'),
  },
  // Clients
  {
    label: t('sidebar.items.clients'),
    icon: 'pi-users',
    path: '/clients',
    group: t('sidebar.groups.clients'),
  },
  {
    label: t('sidebar.items.commerces'),
    icon: 'pi-shop',
    path: '/commerces',
    group: t('sidebar.groups.clients'),
  },
  {
    label: t('sidebar.items.commandes'),
    icon: 'pi-shopping-bag',
    path: '/commandes',
    group: t('sidebar.groups.clients'),
  },
  // Configuration
  {
    label: t('sidebar.items.modules'),
    icon: 'pi-objects-column',
    path: '/commerces',
    group: t('sidebar.groups.configuration'),
  },
  {
    label: t('sidebar.items.medias'),
    icon: 'pi-images',
    path: '/commerces',
    group: t('sidebar.groups.configuration'),
  },
  {
    label: t('sidebar.items.users'),
    icon: 'pi-users',
    path: '/commerces',
    group: t('sidebar.groups.configuration'),
  },
  {
    label: t('sidebar.items.disponibilite'),
    icon: 'pi-clock',
    path: '/commerces/disponibilite',
    group: t('sidebar.groups.configuration'),
  },
  {
    label: t('sidebar.items.traductions'),
    icon: 'pi-language',
    path: '/commerces/traductions',
    group: t('sidebar.groups.configuration'),
  },
  // Paramètres
  {
    label: t('sidebar.items.theme'),
    icon: 'pi-palette',
    path: '/settings/theme',
    group: t('sidebar.groups.parametres'),
  },
  {
    label: t('sidebar.items.notifications'),
    icon: 'pi-bell',
    path: '/notifications',
    group: t('sidebar.groups.parametres'),
  },
  {
    label: t('sidebar.items.paiement'),
    icon: 'pi-credit-card',
    path: '/settings/payment',
    group: t('sidebar.groups.parametres'),
  },
  {
    label: t('sidebar.items.cgu'),
    icon: 'pi-file-edit',
    path: '/settings/terms',
    group: t('sidebar.groups.parametres'),
  },
]);

const results = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return ALL_NAV.value;
  return ALL_NAV.value.filter(
    (s) =>
      s.label.toLowerCase().includes(q) || s.group.toLowerCase().includes(q),
  );
});

function openSearch() {
  searchOpen.value = true;
}
function closeSearch() {
  _closeSearch();
  search.value = '';
}
function pickResult(path: string) {
  router.push(path);
  closeSearch();
}
function onSearchKey(e: KeyboardEvent) {
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    activeIdx.value = Math.min(activeIdx.value + 1, results.value.length - 1);
  }
  if (e.key === 'ArrowUp') {
    e.preventDefault();
    activeIdx.value = Math.max(activeIdx.value - 1, 0);
  }
  if (e.key === 'Enter' && results.value[activeIdx.value])
    pickResult(results.value[activeIdx.value].path);
  if (e.key === 'Escape') closeSearch();
}
function onGlobalKey(e: KeyboardEvent) {
  if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
    e.preventDefault();
    openSearch();
  }
  if (e.key === 'Escape' && searchOpen.value) closeSearch();
}
onMounted(() => {
  window.addEventListener('keydown', onGlobalKey);
  document.addEventListener('click', onDocClick);
});
onUnmounted(() => {
  window.removeEventListener('keydown', onGlobalKey);
  document.removeEventListener('click', onDocClick);
});

// ── Langue ────────────────────────────────────────────────────────────────────
const LANGS = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
];
const showLangMenu = ref(false);
const langWrapRef = ref<HTMLElement | null>(null);
const currentLang = computed(
  () => LANGS.find((l) => l.code === locale.value) ?? LANGS[0],
);

function setLang(code: string) {
  locale.value = code;
  localStorage.setItem('locale', code);
  showLangMenu.value = false;
}

function onDocClick(e: MouseEvent) {
  if (langWrapRef.value && !langWrapRef.value.contains(e.target as Node)) {
    showLangMenu.value = false;
  }
}
</script>

<template>
  <!-- Overlay recherche -->
  <Transition name="search-overlay">
    <div v-if="searchOpen" class="search-backdrop" @click="closeSearch">
      <div class="search-modal" @click.stop>
        <div class="search-modal__bar">
          <i class="pi pi-search search-modal__icon" />
          <input
            ref="searchInput"
            v-model="search"
            class="search-modal__input"
            type="text"
            placeholder="Rechercher une page, un client…"
            @keydown="onSearchKey"
            @input="activeIdx = 0"
          />
          <kbd class="search-modal__esc" @click="closeSearch">Esc</kbd>
        </div>
        <div class="search-modal__results">
          <button
            v-for="(r, i) in results"
            :key="`${r.path}-${i}`"
            class="search-result"
            :class="{ 'search-result--active': i === activeIdx }"
            @mouseenter="activeIdx = i"
            @click="pickResult(r.path)"
          >
            <div class="search-result__icon">
              <i :class="`pi ${r.icon}`" />
            </div>
            <div class="search-result__info">
              <span class="search-result__label">{{ r.label }}</span>
              <span class="search-result__group">{{ r.group }}</span>
            </div>
            <i class="pi pi-arrow-right search-result__arrow" />
          </button>
          <p v-if="results.length === 0" class="search-modal__empty">
            Aucun résultat pour "{{ search }}"
          </p>
        </div>
        <div class="search-modal__footer">
          <span><kbd>↑↓</kbd> Naviguer</span>
          <span><kbd>↵</kbd> Ouvrir</span>
          <span><kbd>Esc</kbd> Fermer</span>
        </div>
      </div>
    </div>
  </Transition>

  <header class="app-header">
    <!-- Mobile hamburger -->
    <button
      class="app-header__hamburger"
      @click="emit('menu-click')"
      aria-label="Menu"
    >
      <i class="pi pi-bars" />
    </button>

    <!-- Retour (historique navigateur) -->
    <button
      class="app-header__back-btn"
      aria-label="Retour"
      @click="router.go(-1)"
    >
      <i class="pi pi-arrow-left" />
    </button>

    <!-- Search bar (déclenche la modal) -->
    <div class="app-header__search-wrap" @click="openSearch">
      <i class="pi pi-search app-header__search-icon" />
      <div
        class="app-header__search-fake"
        role="searchbox"
        tabindex="0"
        @keydown.enter="openSearch"
      >
        Rechercher…
      </div>
      <div class="app-header__search-kbd"><kbd>CTRL</kbd><kbd>K</kbd></div>
    </div>

    <!-- Right actions -->
    <div class="app-header__actions">
      <!-- Notifications (masqué temporairement, fonctionnalité pas encore prête) -->
      <button
        v-if="false"
        class="app-header__icon-btn"
        aria-label="Notifications"
        @click="router.push('/notifications')"
      >
        <i class="pi pi-bell" />
        <span class="app-header__notif-dot" />
      </button>

      <!-- Thème -->
      <button
        class="app-header__icon-btn"
        @click="toggle"
        :aria-label="isDark ? 'Mode clair' : 'Mode sombre'"
      >
        <i :class="isDark ? 'pi pi-sun' : 'pi pi-moon'" />
      </button>

      <!-- Langue -->
      <div class="lang-wrap" ref="langWrapRef">
        <button
          class="app-header__icon-btn lang-btn"
          :aria-label="`Langue : ${currentLang.label}`"
          @click.stop="showLangMenu = !showLangMenu"
        >
          <span class="lang-flag">{{ currentLang.flag }}</span>
        </button>
        <Transition name="menu-drop">
          <div v-if="showLangMenu" class="lang-menu" @click.stop>
            <button
              v-for="l in LANGS"
              :key="l.code"
              class="lang-opt"
              :class="{ 'lang-opt--active': l.code === locale }"
              @click="setLang(l.code)"
            >
              <span class="lang-opt__flag">{{ l.flag }}</span>
              <span class="lang-opt__label">{{ l.label }}</span>
              <i v-if="l.code === locale" class="pi pi-check lang-opt__check" />
            </button>
          </div>
        </Transition>
      </div>

      <div class="app-header__divider" />

      <!-- User -->
      <button
        class="app-header__user app-header__user--btn"
        @click="openProfile"
      >
        <div class="app-header__user-info">
          <p class="app-header__user-name">{{ fullName() }}</p>
          <p class="app-header__user-role">{{ userRole() }}</p>
        </div>
        <div class="app-header__avatar">
          <img v-if="userPhoto()" :src="userPhoto()!" alt="Avatar" />
          <span v-else class="app-header__avatar-fallback">{{
            fullName().charAt(0)
          }}</span>
        </div>
      </button>
    </div>
  </header>

  <!-- Profile drawer backdrop -->
  <Transition name="pdr-fade">
    <div v-if="profileOpen" class="pdr-backdrop" @click="closeProfile" />
  </Transition>

  <!-- Profile drawer -->
  <Transition name="pdr-slide">
    <aside v-if="profileOpen" class="pdr">
      <div class="pdr__header">
        <div class="pdr__header-info">
          <div class="pdr__avatar-wrap">
            <div class="pdr__avatar">
              <img v-if="photoPreview" :src="photoPreview" alt="Avatar" />
              <span v-else class="pdr__avatar-fallback">{{
                fullName().charAt(0)
              }}</span>
            </div>
            <label class="pdr__avatar-edit" :title="t('profile.changePhoto')">
              <i class="pi pi-camera" />
              <input
                type="file"
                accept="image/*"
                class="pdr__file-input"
                @change="onPhotoChange"
              />
            </label>
          </div>
          <div>
            <p class="pdr__name">{{ fullName() }}</p>
            <p class="pdr__role">{{ userRole() }}</p>
          </div>
        </div>
        <button class="pdr__close" @click="closeProfile">
          <i class="pi pi-times" />
        </button>
      </div>

      <div class="pdr__tabs">
        <button
          class="pdr__tab"
          :class="{ 'pdr__tab--active': profileTab === 'info' }"
          @click="profileTab = 'info'"
        >
          <i class="pi pi-user" /> {{ t('profile.tabInfo') }}
        </button>
        <button
          class="pdr__tab"
          :class="{ 'pdr__tab--active': profileTab === 'photo' }"
          @click="profileTab = 'photo'"
        >
          <i class="pi pi-image" /> {{ t('profile.tabPhoto') }}
        </button>
        <button
          class="pdr__tab"
          :class="{ 'pdr__tab--active': profileTab === 'password' }"
          @click="profileTab = 'password'"
        >
          <i class="pi pi-lock" /> {{ t('profile.tabPassword') }}
        </button>
      </div>

      <!-- Info tab -->
      <div v-if="profileTab === 'info'" class="pdr__body">
        <div class="pdr__field">
          <label class="pdr__label">{{ t('profile.firstname') }}</label>
          <input
            v-model="profileForm.firstname"
            class="pdr__input"
            type="text"
          />
        </div>
        <div class="pdr__field">
          <label class="pdr__label">{{ t('profile.lastname') }}</label>
          <input
            v-model="profileForm.lastname"
            class="pdr__input"
            type="text"
          />
        </div>
        <div class="pdr__field">
          <label class="pdr__label">{{ t('profile.email') }}</label>
          <input v-model="profileForm.email" class="pdr__input" type="email" />
        </div>
        <div class="pdr__field">
          <label class="pdr__label">{{ t('profile.phone') }}</label>
          <input v-model="profileForm.phone" class="pdr__input" type="tel" />
        </div>
        <div v-if="profileError" class="pdr__error">
          <i class="pi pi-exclamation-triangle" /> {{ profileError }}
        </div>
        <button
          class="pdr__save-btn"
          :disabled="profileSaving"
          @click="saveProfileInfo"
        >
          <i
            :class="
              profileSaved
                ? 'pi pi-check'
                : profileSaving
                  ? 'pi pi-spin pi-spinner'
                  : 'pi pi-save'
            "
          />
          {{ profileSaved ? t('profile.saved') : t('profile.save') }}
        </button>
      </div>

      <!-- Password tab -->
      <div v-else-if="profileTab === 'password'" class="pdr__body">
        <div class="pdr__field">
          <label class="pdr__label">{{ t('profile.currentPassword') }}</label>
          <input
            v-model="passwordForm.currentSecret"
            class="pdr__input"
            type="password"
            autocomplete="current-password"
            :placeholder="t('profile.currentPasswordPlaceholder')"
          />
        </div>
        <div class="pdr__field">
          <label class="pdr__label">{{ t('profile.newPassword') }}</label>
          <input
            v-model="passwordForm.secret"
            class="pdr__input"
            type="password"
            autocomplete="new-password"
            :placeholder="t('profile.passwordPlaceholder')"
          />
        </div>
        <div class="pdr__field">
          <label class="pdr__label">{{ t('profile.confirmPassword') }}</label>
          <input
            v-model="passwordForm.confirm"
            class="pdr__input"
            type="password"
            autocomplete="new-password"
            :placeholder="t('profile.passwordPlaceholder')"
          />
        </div>
        <div v-if="passwordError" class="pdr__error">
          <i class="pi pi-exclamation-triangle" /> {{ passwordError }}
        </div>
        <button
          class="pdr__save-btn"
          :disabled="passwordSaving"
          @click="savePassword"
        >
          <i
            :class="
              passwordSaved
                ? 'pi pi-check'
                : passwordSaving
                  ? 'pi pi-spin pi-spinner'
                  : 'pi pi-lock'
            "
          />
          {{ passwordSaved ? t('profile.saved') : t('profile.savePassword') }}
        </button>
      </div>

      <!-- Photo tab -->
      <div v-else class="pdr__body">
        <div class="pdr__photo-preview">
          <div class="pdr__photo-circle">
            <img v-if="photoPreview" :src="photoPreview" alt="Preview" />
            <span v-else class="pdr__photo-fallback">{{
              fullName().charAt(0)
            }}</span>
          </div>
        </div>
        <label class="pdr__upload-zone">
          <i class="pi pi-upload" />
          <span>{{ t('profile.choosePhoto') }}</span>
          <input
            type="file"
            accept="image/*"
            class="pdr__file-input"
            @change="onPhotoChange"
          />
        </label>
        <div v-if="photoError" class="pdr__error">
          <i class="pi pi-exclamation-triangle" /> {{ photoError }}
        </div>
        <button
          class="pdr__save-btn"
          :disabled="photoSaving || !photoFile"
          @click="savePhoto"
        >
          <i
            :class="
              photoSaved
                ? 'pi pi-check'
                : photoSaving
                  ? 'pi pi-spin pi-spinner'
                  : 'pi pi-save'
            "
          />
          {{ photoSaved ? t('profile.saved') : t('profile.savePhoto') }}
        </button>
      </div>
    </aside>
  </Transition>
</template>

<style scoped>
/* ── Header — thème Liquid Glass ── */
.app-header {
  height: 5rem;
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0 3rem;
  box-sizing: border-box;
  background: rgba(252, 252, 253, 0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(11, 19, 32, 0.05);
  flex-shrink: 0;
}
@media (max-width: 1279px) {
  .app-header {
    padding: 0 1.5rem;
  }
}
@media (max-width: 767px) {
  .app-header {
    padding: 0 1rem;
    gap: 0.5rem;
  }
}

/* Hamburger */
.app-header__hamburger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.5rem;
  border: 1px solid rgba(11, 19, 32, 0.1);
  background: none;
  font-size: 1rem;
  color: rgba(11, 19, 32, 0.5);
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
}
.app-header__hamburger:hover {
  background: rgba(11, 19, 32, 0.05);
}
@media (min-width: 768px) {
  .app-header__hamburger {
    display: none;
  }
}

/* Retour */
.app-header__back-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  min-width: 2.25rem;
  border-radius: 50%;
  border: 1px solid rgba(11, 19, 32, 0.1);
  background: none;
  font-size: 1rem;
  color: rgba(11, 19, 32, 0.5);
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background 0.15s,
    color 0.15s;
}
.app-header__back-btn:hover {
  background: rgba(11, 19, 32, 0.05);
  color: #0b1320;
}

/* Search trigger */
.app-header__search-wrap {
  flex: 1;
  min-width: 0;
  max-width: 32rem;
  position: relative;
  cursor: pointer;
}
@media (max-width: 479px) {
  .app-header__search-wrap {
    flex: unset;
  }
  .app-header__search-fake {
    display: none;
  }
  .app-header__search-icon {
    position: static;
    transform: none;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 50%;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.125rem;
    pointer-events: auto;
    cursor: pointer;
  }
}
@media (min-width: 480px) {
  .app-header__search-icon {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.9rem;
    color: rgba(11, 19, 32, 0.3);
    pointer-events: none;
  }
  .app-header__search-fake {
    width: 100%;
    height: 2.625rem;
    padding: 0 3.5rem 0 2.75rem;
    border: none;
    border-radius: 9999px;
    background: rgba(11, 19, 32, 0.05);
    font-size: 0.875rem;
    font-family: inherit;
    color: rgba(11, 19, 32, 0.35);
    display: flex;
    align-items: center;
    box-sizing: border-box;
    transition:
      background 0.15s,
      box-shadow 0.15s;
    user-select: none;
  }
  .app-header__search-wrap:hover .app-header__search-fake {
    background: rgba(11, 19, 32, 0.08);
    box-shadow: 0 0 0 2px rgba(255, 214, 10, 0.4);
  }
}
.app-header__search-kbd {
  position: absolute;
  right: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  gap: 0.2rem;
  opacity: 0.3;
  pointer-events: none;
}
.app-header__search-kbd kbd {
  padding: 0.1rem 0.35rem;
  background: #fff;
  border: 1px solid rgba(11, 19, 32, 0.1);
  border-radius: 0.25rem;
  font-size: 0.5rem;
  font-weight: 700;
  font-family: inherit;
  color: rgba(11, 19, 32, 0.5);
}
@media (max-width: 640px) {
  .app-header__search-kbd {
    display: none;
  }
}

/* Actions */
.app-header__actions {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-shrink: 0;
  margin-left: auto;
}
@media (max-width: 479px) {
  .app-header__actions > .app-header__icon-btn:first-child {
    display: none;
  }
}
@media (max-width: 360px) {
  .lang-wrap {
    display: none;
  }
}

.app-header__icon-btn {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  border: none;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
  color: rgba(11, 19, 32, 0.4);
  cursor: pointer;
  position: relative;
  transition:
    color 0.15s,
    background 0.15s;
}
.app-header__icon-btn:hover {
  color: #0b1320;
  background: rgba(11, 19, 32, 0.05);
}
.app-header__notif-dot {
  position: absolute;
  top: 0.35rem;
  right: 0.35rem;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: #ef4444;
  border: 2px solid rgba(252, 252, 253, 0.9);
}
.app-header__divider {
  width: 1px;
  height: 1.5rem;
  background: rgba(11, 19, 32, 0.08);
  margin: 0 0.5rem;
}
@media (max-width: 480px) {
  .app-header__divider {
    display: none;
  }
}

/* Langue */
.lang-wrap {
  position: relative;
}
.lang-btn {
  font-size: 1.125rem;
}
.lang-flag {
  font-size: 1.125rem;
  line-height: 1;
}

.lang-menu {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  background: #fff;
  border: 1px solid rgba(11, 19, 32, 0.08);
  border-radius: 0.875rem;
  box-shadow: 0 8px 32px rgba(11, 19, 32, 0.1);
  padding: 0.375rem;
  min-width: 11rem;
  z-index: 100;
}
.lang-opt {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: none;
  border-radius: 0.5rem;
  background: none;
  font-size: 0.875rem;
  font-weight: 500;
  font-family: inherit;
  color: rgba(11, 19, 32, 0.6);
  cursor: pointer;
  text-align: left;
  transition:
    background 0.12s,
    color 0.12s;
}
.lang-opt:hover {
  background: rgba(11, 19, 32, 0.04);
  color: #0b1320;
}
.lang-opt--active {
  color: #0b1320;
  font-weight: 700;
}
.lang-opt__flag {
  font-size: 1rem;
}
.lang-opt__label {
  flex: 1;
}
.lang-opt__check {
  font-size: 0.75rem;
  color: #ffd60a;
}

.menu-drop-enter-active,
.menu-drop-leave-active {
  transition:
    opacity 0.15s,
    transform 0.15s;
}
.menu-drop-enter-from,
.menu-drop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* User */
.app-header__user {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}
.app-header__user--btn {
  border: none;
  background: none;
  cursor: pointer;
  font-family: inherit;
  padding: 0.375rem 0.625rem;
  border-radius: 0.75rem;
  transition: background 0.15s;
  display: flex;
  align-items: center;
  gap: 0.625rem;
}
.app-header__user--btn:hover {
  background: rgba(11, 19, 32, 0.04);
}
.app-header__user-info {
  text-align: right;
}
.app-header__user-name {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #0b1320;
  line-height: 1.3;
  white-space: nowrap;
}
.app-header__user-role {
  margin: 0;
  font-size: 0.625rem;
  color: rgba(11, 19, 32, 0.4);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
@media (max-width: 480px) {
  .app-header__user-info {
    display: none;
  }
}

.app-header__avatar {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid rgba(255, 214, 10, 0.3);
  background: rgba(11, 19, 32, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.app-header__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.app-header__avatar-fallback {
  font-size: 0.875rem;
  font-weight: 800;
  color: #0b1320;
}
</style>

<style>
/* ── Profile Drawer ── */
.pdr-backdrop {
  position: fixed;
  inset: 0;
  z-index: 300;
  background: rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(2px);
}
.pdr-fade-enter-active,
.pdr-fade-leave-active {
  transition: opacity 0.25s ease;
}
.pdr-fade-enter-from,
.pdr-fade-leave-to {
  opacity: 0;
}

.pdr {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 301;
  width: min(380px, 92vw);
  background: var(--nxt-card-bg, #fff);
  border-left: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.1));
  box-shadow: -8px 0 40px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.pdr-slide-enter-active,
.pdr-slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.2, 1, 0.3, 1);
}
.pdr-slide-enter-from,
.pdr-slide-leave-to {
  transform: translateX(100%);
}

.pdr__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.5rem 1.25rem 1rem;
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
}
.pdr__header-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}
.pdr__avatar-wrap {
  position: relative;
  flex-shrink: 0;
}
.pdr__avatar {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  display: flex;
  align-items: center;
  justify-content: center;
}
.pdr__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.pdr__avatar-fallback {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--nxt-primary);
}
.pdr__avatar-edit {
  position: absolute;
  bottom: -4px;
  right: -4px;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background: var(--nxt-primary, #141c29);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6rem;
  cursor: pointer;
  border: 2px solid #fff;
}
.pdr__name {
  margin: 0 0 0.125rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.pdr__role {
  margin: 0;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
}
.pdr__close {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: none;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  font-size: 0.875rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s;
}
.pdr__close:hover {
  background: var(--nxt-surface-high);
}

.pdr__tabs {
  display: flex;
  gap: 0.25rem;
  padding: 0.75rem 1.25rem 0;
  border-bottom: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
}
.pdr__tab {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  border: none;
  border-radius: 0.5rem 0.5rem 0 0;
  background: none;
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: color 0.15s;
}
.pdr__tab--active {
  color: var(--nxt-primary, #141c29);
  border-bottom-color: var(--nxt-primary, #141c29);
}

.pdr__body {
  flex: 1;
  overflow-y: auto;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.pdr__field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.pdr__label {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--nxt-on-surface-variant);
}
.pdr__input {
  height: 2.5rem;
  border-radius: 0.625rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.12));
  background: var(--nxt-surface-low, #f8f9fa);
  padding: 0 0.875rem;
  font-size: 0.875rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
  outline: none;
  transition: border-color 0.15s;
  width: 100%;
  box-sizing: border-box;
}
.pdr__input:focus {
  border-color: var(--nxt-primary);
}

.pdr__error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.5rem;
  background: rgba(186, 26, 26, 0.06);
  border: 1px solid rgba(186, 26, 26, 0.15);
  color: #ba1a1a;
  font-size: 0.8125rem;
}
.pdr__save-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 2.75rem;
  border-radius: 0.875rem;
  border: none;
  background: #ffd60a;
  color: #0b1320;
  font-size: 0.875rem;
  font-weight: 800;
  font-family: inherit;
  cursor: pointer;
  transition:
    background 0.15s,
    box-shadow 0.15s,
    transform 0.15s;
  margin-top: 0.5rem;
  flex-shrink: 0;
  width: 100%;
  box-shadow: 0 2px 8px rgba(255, 214, 10, 0.3);
}
.pdr__save-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  box-shadow: none;
}
.pdr__save-btn:not(:disabled):hover {
  background: #e6c109;
  box-shadow: 0 6px 16px rgba(255, 214, 10, 0.4);
  transform: translateY(-1px);
}

.pdr__photo-preview {
  display: flex;
  justify-content: center;
  padding: 1rem 0;
}
.pdr__photo-circle {
  width: 7rem;
  height: 7rem;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  display: flex;
  align-items: center;
  justify-content: center;
}
.pdr__photo-circle img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.pdr__photo-fallback {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--nxt-primary);
}

.pdr__upload-zone {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  padding: 1.25rem;
  border-radius: 0.875rem;
  border: 2px dashed var(--nxt-outline-variant, rgba(0, 0, 0, 0.15));
  background: var(--nxt-surface-low);
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--nxt-on-surface-variant);
  transition:
    border-color 0.15s,
    background 0.15s;
  position: relative;
}
.pdr__upload-zone:hover {
  border-color: var(--nxt-primary);
  background: rgba(20, 28, 41, 0.03);
}
.pdr__file-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
  width: 100%;
  height: 100%;
}

/* Dark mode */
[data-theme='dark'] .pdr {
  background: #111c2e;
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: -8px 0 40px rgba(0, 0, 0, 0.4);
}
[data-theme='dark'] .pdr__header,
[data-theme='dark'] .pdr__tabs {
  border-color: rgba(255, 255, 255, 0.07);
}
[data-theme='dark'] .pdr__name {
  color: #f1f5f9;
}
[data-theme='dark'] .pdr__role {
  color: rgba(255, 255, 255, 0.4);
}
[data-theme='dark'] .pdr__close {
  background: rgba(255, 255, 255, 0.07);
  color: rgba(255, 255, 255, 0.5);
}
[data-theme='dark'] .pdr__close:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}
[data-theme='dark'] .pdr__tab {
  color: rgba(255, 255, 255, 0.4);
}
[data-theme='dark'] .pdr__tab--active {
  color: #ffd60a;
  border-bottom-color: #ffd60a;
}
[data-theme='dark'] .pdr__label {
  color: rgba(255, 255, 255, 0.4);
}
[data-theme='dark'] .pdr__input {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #f1f5f9;
}
[data-theme='dark'] .pdr__input:focus {
  border-color: #ffd60a;
}
[data-theme='dark'] .pdr__save-btn {
  background: #ffd60a;
  color: #0b1320;
  box-shadow: 0 2px 12px rgba(255, 214, 10, 0.25);
}
[data-theme='dark'] .pdr__save-btn:not(:disabled):hover {
  background: #ffe033;
  box-shadow: 0 6px 20px rgba(255, 214, 10, 0.35);
}
[data-theme='dark'] .pdr__upload-zone {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.45);
}
[data-theme='dark'] .pdr__upload-zone:hover {
  border-color: #ffd60a;
  background: rgba(255, 214, 10, 0.04);
}
[data-theme='dark'] .pdr__avatar {
  border-color: rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .pdr__avatar-fallback {
  color: #ffd60a;
}
[data-theme='dark'] .pdr__avatar-edit {
  background: #ffd60a;
  color: #0b1320;
  border-color: #111c2e;
}
[data-theme='dark'] .pdr__photo-circle {
  border-color: rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.05);
}
[data-theme='dark'] .pdr__photo-fallback {
  color: #ffd60a;
}
[data-theme='dark'] .pdr__body {
  scrollbar-color: rgba(255, 255, 255, 0.12) transparent;
}
</style>

<style>
/* ── Dark — header ── */
[data-theme='dark'] .app-header {
  background: rgba(12, 20, 33, 0.8);
  border-color: rgba(255, 255, 255, 0.05);
}
[data-theme='dark'] .app-header__hamburger {
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.5);
}
[data-theme='dark'] .app-header__hamburger:hover {
  background: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .app-header__back-btn {
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.5);
}
[data-theme='dark'] .app-header__back-btn:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
}
[data-theme='dark'] .app-header__search-fake {
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.2);
}
[data-theme='dark'] .app-header__search-icon {
  color: rgba(255, 255, 255, 0.3);
}
[data-theme='dark'] .app-header__search-kbd kbd {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.4);
}
[data-theme='dark'] .app-header__icon-btn {
  color: rgba(255, 255, 255, 0.4);
}
[data-theme='dark'] .app-header__icon-btn:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .app-header__divider {
  background: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .app-header__notif-dot {
  border-color: rgba(12, 20, 33, 0.8);
}
[data-theme='dark'] .app-header__user-name {
  color: #ffffff;
}
[data-theme='dark'] .app-header__user-role {
  color: rgba(255, 255, 255, 0.4);
}
[data-theme='dark'] .app-header__user--btn:hover {
  background: rgba(255, 255, 255, 0.05);
}
[data-theme='dark'] .app-header__avatar {
  border-color: rgba(255, 214, 10, 0.2);
  background: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .app-header__avatar-fallback {
  color: #ffd60a;
}
[data-theme='dark'] .lang-menu {
  background: #111c2e;
  border-color: rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}
[data-theme='dark'] .lang-opt {
  color: rgba(255, 255, 255, 0.5);
}
[data-theme='dark'] .lang-opt:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
}
[data-theme='dark'] .lang-opt--active {
  color: #ffd60a;
}

/* ── Search modal (global, pas scoped) ── */
.search-backdrop {
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 8vh;
}
.search-overlay-enter-active,
.search-overlay-leave-active {
  transition: opacity 0.2s ease;
}
.search-overlay-enter-from,
.search-overlay-leave-to {
  opacity: 0;
}
.search-overlay-enter-active .search-modal,
.search-overlay-leave-active .search-modal {
  transition:
    transform 0.2s ease,
    opacity 0.2s ease;
}
.search-overlay-enter-from .search-modal {
  transform: translateY(-12px);
  opacity: 0;
}
.search-overlay-leave-to .search-modal {
  transform: translateY(-12px);
  opacity: 0;
}

.search-modal {
  width: min(620px, 92vw);
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.18);
  overflow: hidden;
}
.search-modal__bar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #f0f0f0;
}
.search-modal__icon {
  font-size: 1.125rem;
  color: #888;
  flex-shrink: 0;
}
.search-modal__input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 1rem;
  font-family: inherit;
  color: #111;
}
.search-modal__esc {
  padding: 0.15rem 0.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 0.375rem;
  font-size: 0.6875rem;
  font-weight: 700;
  font-family: inherit;
  background: #f7f7f7;
  color: #666;
  cursor: pointer;
  flex-shrink: 0;
}
.search-modal__results {
  padding: 0.5rem;
  max-height: 26rem;
  overflow-y: auto;
}
.search-result {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 0.625rem;
  background: none;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.1s;
}
.search-result--active {
  background: #fff7ee;
}
.search-result__icon {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9375rem;
  color: #666;
  flex-shrink: 0;
}
.search-result--active .search-result__icon {
  background: rgba(0, 0, 0, 0.1);
  color: #111111;
}
.search-result__info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}
.search-result__label {
  font-size: 0.9375rem;
  font-weight: 500;
  color: #111;
}
.search-result__group {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #999;
}
.search-result__arrow {
  font-size: 0.75rem;
  color: #111111;
  opacity: 0;
  transition: opacity 0.1s;
}
.search-result--active .search-result__arrow {
  opacity: 1;
}
.search-modal__empty {
  padding: 1.5rem;
  text-align: center;
  color: #888;
  font-size: 0.875rem;
  margin: 0;
}
.search-modal__footer {
  display: flex;
  gap: 1.25rem;
  align-items: center;
  padding: 0.625rem 1.25rem;
  border-top: 1px solid #f0f0f0;
  font-size: 0.6875rem;
  color: #888;
}
.search-modal__footer kbd {
  padding: 0.1rem 0.35rem;
  border: 1px solid #e0e0e0;
  border-radius: 0.25rem;
  font-size: 0.6rem;
  font-weight: 700;
  font-family: inherit;
  background: #f7f7f7;
  color: #555;
  margin-right: 0.25rem;
}

/* Dark pour search modal */
[data-theme='dark'] .search-modal {
  background: #1e2022;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
}
[data-theme='dark'] .search-modal__bar {
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .search-modal__input {
  color: #f0f0f0;
}
[data-theme='dark'] .search-modal__icon {
  color: #aaa;
}
[data-theme='dark'] .search-modal__esc {
  background: #2a2d30;
  border-color: rgba(255, 255, 255, 0.1);
  color: #aaa;
}
[data-theme='dark'] .search-result__label {
  color: #f0f0f0;
}
[data-theme='dark'] .search-result__group {
  color: #555;
}
[data-theme='dark'] .search-result--active {
  background: rgba(0, 0, 0, 0.08);
}
[data-theme='dark'] .search-result__icon {
  background: #2a2d30;
  color: #aaa;
}
[data-theme='dark'] .search-result--active .search-result__icon {
  background: rgba(0, 0, 0, 0.12);
  color: #e0e0e0;
}
[data-theme='dark'] .search-modal__footer {
  border-color: rgba(255, 255, 255, 0.08);
  color: #666;
}
[data-theme='dark'] .search-modal__footer kbd {
  background: #2a2d30;
  border-color: rgba(255, 255, 255, 0.1);
  color: #888;
}
[data-theme='dark'] .search-modal__empty {
  color: #666;
}
</style>
